import yaml from 'js-yaml';
import React from 'react';

import type { TabOption } from '@/components/chariot-ui';
import type {
  MainTabKey,
  POEContent,
  SectionContent,
  Template,
} from '@/sections/poe/types/poe.types';
import { mapToMainTab } from '@/sections/poe/utils/mapToMainTab';
import { pluralize } from '@/utils/pluralize.util';

const NUCLEI_FIELD_MAPPINGS = [
  { key: 'request', label: 'Request' },
  { key: 'curl-command', label: 'cURL Command', value: 'curl_command' },
  { key: 'response', label: 'Response' },
  { key: 'error', label: 'Error' },
  { key: 'matched-at', label: 'Match', value: 'matched_at' },
  { key: 'found_at_url', label: 'Found At URL', value: 'found_at_url' },
  { key: 'resolved_to', label: 'Resolved To', value: 'resolved_to' },
  { key: 'detector_name', label: 'Detector', value: 'detector_name' },
  { key: 'matched_at', label: 'Matched At', value: 'matched_at' },
  { key: 'snippet', label: 'Matched Snippet', value: 'snippet' },
  { key: 'input_fields', label: 'Input Fields', value: 'input_fields' },
  { key: 'extracted-results', label: 'Extracted Results' },
  {
    key: 'template-encoded',
    label: 'Template',
    value: 'template_encoded',
    transform: (val: string) => {
      try {
        return atob(val);
      } catch {
        return val;
      }
    },
  },
];

const getMatchCriteria = (poeData: POEContent): React.ReactNode | null => {
  const templateContent = poeData['template-encoded'];
  if (!templateContent) {
    return null;
  }

  try {
    const decodedTemplate = atob(templateContent as string);

    try {
      const template = yaml.load(decodedTemplate) as Template;
      const matchers = template?.http?.[0]?.matchers || [];

      if (matchers && matchers.length > 0) {
        return (
          <pre className="flex flex-col gap-2">
            {matchers.map(matcher => {
              const conditionParts = (matcher?.[matcher.type] ||
                matcher?.[pluralize(2, matcher.type)] ||
                []) as string[];

              return (
                <div
                  className="border-chariot-stroke w-full rounded-md border p-2 text-sm"
                  key={matcher.type}
                >
                  <h3>{`${matcher.type} ${matcher.part ? `(${matcher.part})` : ''}:`}</h3>
                  <p className="text-wrap">
                    {conditionParts.join(` ${matcher.condition} `)}
                  </p>
                </div>
              );
            })}
          </pre>
        );
      }
    } catch {
      // Fallback to regex if yaml parsing fails
    }
  } catch (e) {
    console.error('Error parsing template for match criteria:', e);
  }

  return null;
};

export const processNucleiDataSource = (
  poeData: POEContent,
  sections: Record<string, SectionContent>,
  tempTabs: Record<MainTabKey, TabOption[]>,
  mainTabKeys: MainTabKey[]
) => {
  if (!poeData) return;
  NUCLEI_FIELD_MAPPINGS.forEach(({ key, label, value, transform }) => {
    const content = poeData[key as keyof POEContent];
    if (content === undefined) return;
    let contentStr;
    if (typeof content === 'object') {
      contentStr = JSON.stringify(content, null, 2);
    } else {
      try {
        const parsed = JSON.parse(content as string);
        contentStr = JSON.stringify(parsed, null, 2);
      } catch {
        contentStr = content as string;
      }
    }
    const sectionKey = value || key.toLowerCase().replace('-', '_');
    const mainTab = mapToMainTab(label, mainTabKeys);
    tempTabs[mainTab].push({
      label,
      value: sectionKey,
    });
    sections[sectionKey] = transform ? transform(contentStr) : contentStr;

    // Handle 'matched-at' special case inside the forEach
    if (key === 'matched-at') {
      const matchCriteriaSection = getMatchCriteria(poeData);
      if (matchCriteriaSection) {
        sections['match_criteria'] = matchCriteriaSection;
        if (mainTabKeys.includes('DETECTION')) {
          tempTabs['DETECTION'].push({
            label: 'Match Criteria',
            value: 'match_criteria',
          });
        }
      }
    }
  });
};
